import React from 'react';
import { Form, Radio } from 'antd';
import { FormItem } from '@xbcb/form-item-components';
import { shouldUpdate, getCodes } from '@xbcb/ui-utils';
import { clearFields } from 'libs/clearFields';
import { useTheme } from 'styled-components';
import { NamePath } from '@xbcb/ui-types';
import DisclaimerSelect from 'components/DisclaimerSelect';
import CpscReferencePgaMessageSet from 'components/CpscReferencePgaMessageSet';
import CpscComponentTabs from 'components/CpscComponentTabs';
import CpscFullPgaMessageSet from 'components/CpscFullPgaMessageSet';
import {
  CpscCertificateType,
  CpscGlobalIdentifierType,
} from '__generated__/graphql';
import {
  StyledCpscCoreFormDiv,
  StyledRootFieldsDiv,
  StyledSelect,
  StyledInput,
  StyledAddonSelect,
} from './styles';

const PROCESSING_TYPES = {
  DISCLAIM: 'DISCLAIM',
  REF: 'REF',
  FULL: 'FULL',
} as const;

const CERTIFICATE_TYPE_OPTIONS = [
  {
    value: CpscCertificateType.CPC,
    label: "CPC - Children's Product Certificate",
  },
  {
    value: CpscCertificateType.GCC,
    label: 'GCC - General Certificate of Conformity',
  },
];

const GLOBAL_IDENTIFIER_TYPE_OPTIONS = [
  {
    value: CpscGlobalIdentifierType.GTIN,
    label: 'GTIN',
    title: 'Global Trade Item Number',
  },
  {
    value: CpscGlobalIdentifierType.UPC,
    label: 'UPC',
    title: 'Universal Product Code',
  },
];

// Props interface for tab content components
interface TabContentProps {
  localNamePath: NamePath;
  fullNamePath: NamePath;
  readOnly?: boolean;
  disabled?: boolean;
}

// Component Map by processing type (module level constant)
const TAB_COMPONENT_MAP: Record<string, React.FC<TabContentProps>> = {
  [PROCESSING_TYPES.REF]: CpscReferencePgaMessageSet,
  [PROCESSING_TYPES.FULL]: CpscFullPgaMessageSet,
};

export interface CpscCoreFormProps {
  fullNamePath: NamePath;
  localNamePath: NamePath;
  readOnly?: boolean;
  required?: boolean;
  disabled?: boolean;
}

const CpscCoreForm: React.FC<CpscCoreFormProps> = ({
  fullNamePath,
  localNamePath,
  readOnly,
  required,
  disabled,
}) => {
  const codes = getCodes();
  const { flag: pgaFlags, disclaimer: disclaimerCodes } = codes.CBP.CATAIR.PGA;
  const form = Form.useFormInstance();
  const theme = useTheme();

  const cpscFlag = pgaFlags.CP1;
  const filterDisclaimerCodes = cpscFlag?.disclaimerCodes;
  return (
    <StyledCpscCoreFormDiv>
      <div className="radio-group-horizontal">
        <FormItem
          name={[...localNamePath, 'processingType']}
          rules={[{ required, message: ' ' }]}
          $readOnly={readOnly}
          $itemSize={theme.size.short}
        >
          <Radio.Group
            disabled={disabled || readOnly}
            onChange={clearFields({
              form,
              fullNamePath,
              fields: [
                'disclaimerCode',
                'productRegistryId',
                'productRegistryVersion',
                'lcpoNumber',
              ],
            })}
          >
            <Radio value={PROCESSING_TYPES.DISCLAIM}>Disclaim</Radio>
            <Radio value={PROCESSING_TYPES.REF}>
              Reference PGA message set
            </Radio>
            <Radio value={PROCESSING_TYPES.FULL}>Full PGA message set</Radio>
          </Radio.Group>
        </FormItem>
      </div>

      {/* Conditional content based on processing type */}
      <Form.Item
        shouldUpdate={shouldUpdate([[...fullNamePath, 'processingType']])}
        noStyle
      >
        {() => {
          const processingType = form.getFieldValue([
            ...fullNamePath,
            'processingType',
          ]);
          const TabContentComponent = TAB_COMPONENT_MAP[processingType];
          const showRootFields = processingType === PROCESSING_TYPES.FULL;

          return (
            <>
              {/* Certificate Type and Global Identifier - shown for FULL only */}
              {showRootFields && (
                <StyledRootFieldsDiv>
                  <FormItem
                    name={[...localNamePath, 'certificateType']}
                    label="Certificate type"
                    $itemSize={theme.size.short_medium}
                    $readOnly={readOnly}
                    rules={[{ required: true }]}
                    validateTrigger="onBlur"
                  >
                    <StyledSelect
                      options={CERTIFICATE_TYPE_OPTIONS}
                      disabled={disabled || readOnly}
                      placeholder="Select certificate type"
                    />
                  </FormItem>
                  <FormItem
                    name={[...localNamePath, 'globalIdentifier', 'code']}
                    label="Global Identifier"
                    $itemSize={theme.size.short_medium}
                    $readOnly={readOnly}
                  >
                    <StyledInput
                      addonBefore={
                        <Form.Item
                          name={[...localNamePath, 'globalIdentifier', 'type']}
                          noStyle
                        >
                          <StyledAddonSelect
                            options={GLOBAL_IDENTIFIER_TYPE_OPTIONS}
                            disabled={disabled || readOnly}
                            placeholder="Type"
                            allowClear
                          />
                        </Form.Item>
                      }
                      maxLength={19}
                      disabled={disabled || readOnly}
                      placeholder="Enter code"
                    />
                  </FormItem>
                </StyledRootFieldsDiv>
              )}

              {/* Disclaim reason - shown for DISCLAIM only */}
              {processingType === PROCESSING_TYPES.DISCLAIM && (
                <DisclaimerSelect
                  localNamePath={[...localNamePath, 'disclaimerCode']}
                  label="Disclaim reason"
                  disclaimerCodes={disclaimerCodes}
                  filter={filterDisclaimerCodes}
                  required={required}
                  readOnly={readOnly}
                  disabled={disabled}
                />
              )}

              {/* Component Tabs - shown for REF and FULL */}
              {TabContentComponent && (
                <FormItem
                  name={[...localNamePath, 'items']}
                  initialValue={[{}]}
                >
                  <CpscComponentTabs
                    readOnly={readOnly}
                    disabled={disabled}
                    renderTabContent={(index) => (
                      <TabContentComponent
                        localNamePath={[...localNamePath, 'items', index]}
                        fullNamePath={[...fullNamePath, 'items', index]}
                        readOnly={readOnly}
                        disabled={disabled}
                      />
                    )}
                  />
                </FormItem>
              )}
            </>
          );
        }}
      </Form.Item>
    </StyledCpscCoreFormDiv>
  );
};

export default CpscCoreForm;
